import React, { useMemo, useState } from "react";
import WidgetCard from "./WidgetCard";
import { Badge, Button, Col, Dropdown, Radio, Row, Select, Space } from "antd";
import { BsArrowRight } from "react-icons/bs";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { CategoryOverviewMSTrendsOverTimeOptions, COLORS } from "@ts/constants";
import { AVAILABILITY_CHARTS, CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO, CATEGORY_OVERVIEW_SKU_FILTERS, ITrendsWithCompareModalWidget, PricingCharts, ProductDeepdiveModules } from "@ts/common-types";
import FiltersMenu from "../drop-downs/FiltersMenu";
import RevenueUnitsFilterMenu from "../misc/RevenueUnitsFilterMenu";
import { internationalizeNumericValue } from "@/ts/utils";
const { Option } = Select;

const TrendsWithCompareModalWidget: React.FC<ITrendsWithCompareModalWidget> = ({
    title,
    widgetData,
    onWidgetLinkHandler,
    widgetLinkName,
    graphAttributes,
    additionalTitleComponent,
    filterPillsComponent,
    compare = true,
    selectedSku,
    maxSelectableSkus = 2,
    maxDates,
    chart,
    showRadioFilter = false,
    showFilter = false,
    selectedRadioFilters = "",
    setSelectedRadioFilters = () => { },
    selectedMSTrendsOvertimeRadio,
    setSelectedMSTrendsOvertimeRadio,
    showDataLabels = true,
    yAxisGraphType = "column",

}) => {
    const allSkus = widgetData?.categories || [];
    const selectableSkus = allSkus.filter((sku) => sku !== selectedSku);
    const defaultSkus = selectableSkus.slice(0, maxSelectableSkus);
    const [selectedSkus, setSelectedSkus] = useState<string[]>(defaultSkus);


    const filteredWidgetData = useMemo(() => {
        const selectedCategories = [selectedSku, ...selectedSkus]; // Always include selectedSku

        if (chart === AVAILABILITY_CHARTS.MY_PRODUCT_VS_COMPETITION) {
            return {
                categories: widgetData.categories.filter((_, index) =>
                    selectedCategories.includes(widgetData.categories[index])
                ),
                yAxisData: widgetData.yAxisData.filter((_, index) =>
                    selectedCategories.includes(widgetData.categories[index])
                ),
                statusData: widgetData.statusData.filter((_, index) =>
                    selectedCategories.includes(widgetData.categories[index])
                ),
            };
        }

        if (chart === AVAILABILITY_CHARTS.AVAILABILITY_TRENDS_OVERTIME) {
            // Limit data to the first 5 entries
            return {
                categories: widgetData?.categories.slice(0, maxDates),
                yAxisData: widgetData?.yAxisData.slice(0, maxDates),
                yAxisOppositeData: widgetData?.yAxisOppositeData?.slice(0, maxDates),
            };
        }
        return widgetData

    }, [selectedSkus, selectedSku, widgetData, chart]);




    const chartOptions = useMemo(() => {
        const hasOppositeAxis = filteredWidgetData && 'yAxisOppositeData' in filteredWidgetData && !!filteredWidgetData.yAxisOppositeData;
        const isXAxisDateTime = graphAttributes?.xAxisType === "datetime";
        return {
            chart: {
                type: "column",
            },
            colors: COLORS,
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                type: isXAxisDateTime ? "datetime" : "category",
                title: { text: graphAttributes?.hideXAxisTitle ? "" : (graphAttributes?.xAxisTitle || "Date"), style: { fontWeight: "normal" } },
                categories: filteredWidgetData?.categories,
            },
            yAxis: [
                {
                    title: {
                        text: graphAttributes?.yAxisTitle || "#Ratings",
                        style: { fontWeight: "normal" },
                    },
                    labels: { style: { fontWeight: "normal" } },
                },
                ...(hasOppositeAxis
                    ? [
                        {
                            title: {
                                text: graphAttributes?.yAxisOppositeTitle || "#Average Rating",
                                style: { fontWeight: "normal" },
                            },
                            opposite: true,
                            labels: { style: { fontWeight: "normal" } },
                        },
                    ]
                    : []),
            ],
            legend: { itemStyle: { fontWeight: "normal" } },
            tooltip: {
                shared: true,
                useHTML: true,
                zIndex: 100,
                backgroundColor: "#fff",
                borderWidth: 1,
                pointFormatter: function (this: Highcharts.Point) {
                    const seriesName = this.series.name;
                    const value = internationalizeNumericValue(this.y);
                    return `<span style="color:${this.color}">\u25CF</span> <b>${seriesName}</b>: ${value}<br/>`;
                }
            },
            series: [
                {
                    name: graphAttributes?.yAxisTitle,
                    type: yAxisGraphType,
                    data: filteredWidgetData?.yAxisData?.map((value, index) => ({
                        y: value,
                        status: filteredWidgetData?.statusData?.[index],
                    })),
                    yAxis: 0,
                    connectNulls: true,
                    ...(!hasOppositeAxis && showDataLabels ? {
                        dataLabels: {
                            enabled: true,
                            formatter: function (this: any) {
                                const status = widgetData?.statusData?.[this.point.index] === "available";
                                if (status === undefined) return "";

                                const color = status ? "green" : "red";
                                const label = status ? "Available" : "Not Available";

                                return `<div style="display: flex; flex-direction: column; align-items: center; gap: 6px; font-weight: bold; font-size: 14px;">
                                        <div>${label}</div>
                                        <div style="font-size: 35px; color: ${color}; line-height: 0;">●</div>
                                    </div>`;
                            },
                            useHTML: true,
                            verticalAlign: "top",
                            y: -45,
                            align: "center",
                            zIndex: 1
                        }
                    } : {}),
                },
                ...(hasOppositeAxis
                    ? [
                        {
                            name: graphAttributes?.yAxisOppositeTitle,
                            type: "line",
                            data: filteredWidgetData?.yAxisOppositeData,
                            yAxis: 1,
                            connectNulls: true,
                            marker: { enabled: true },
                            ...(graphAttributes?.yAxisOppositeDashStyleReq ? {
                                dashStyle: "Dash",
                                color: graphAttributes?.yAxisOppositeDashLineColor ? graphAttributes?.yAxisOppositeDashLineColor : ""
                            } : {}),
                        },
                        ...(graphAttributes?.yAxisOppositeTitleOptional
                            ? [
                                {
                                    name: graphAttributes?.yAxisOppositeTitleOptional,
                                    type: "line",
                                    data: filteredWidgetData?.yAxisOppositeDataOptional,
                                    yAxis: 0,
                                    connectNulls: true,
                                },
                            ]
                            : []),
                    ]
                    : []),
            ],
        }

    }, [graphAttributes, filteredWidgetData]);

    const handleSkuChange = (selected: string[]) => {
        setSelectedSkus(selected.slice(0, maxSelectableSkus));
    };

    const isOnlyOneChartFilter = chart === AVAILABILITY_CHARTS.MY_PRODUCT_VS_COMPETITION

    return (
        <WidgetCard className="my-4">
            <Row className="mb-3 flex-wrap" align="middle">
                <Col xs={24} md={isOnlyOneChartFilter ? 19 : 8} className="mb-2 mb-md-0">
                    <h5>{title || "Ratings and Reviews Trends"}</h5>
                </Col>
                {chart === AVAILABILITY_CHARTS.MY_PRODUCT_VS_COMPETITION ?
                    <Col xs={24} md={5} className="mb-2 mb-md-0 ">
                        <Select
                            mode="multiple"
                            value={selectedSkus}
                            onChange={handleSkuChange}
                            className="w-100"
                            maxTagCount={2}
                            placeholder="Select SKUs to compare"
                        >
                            {selectableSkus.map((sku: string) => (
                                <Option
                                    key={sku}
                                    value={sku}
                                    disabled={
                                        selectedSkus.length >= maxSelectableSkus &&
                                        !selectedSkus.includes(sku)
                                    }
                                >
                                    {sku}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    : ""}

                <Col xs={24} md={16} className="d-flex align-items-center justify-content-end w-100">

                    {
                        showRadioFilter && <Radio.Group
                            defaultValue={selectedMSTrendsOvertimeRadio}
                            buttonStyle="solid"
                            onChange={(e) => {
                                setSelectedMSTrendsOvertimeRadio(e.target.value);
                            }}
                        >
                            {
                                CategoryOverviewMSTrendsOverTimeOptions.map((item) =>
                                (
                                    <Radio.Button value={item.value}>{item.label}</Radio.Button>
                                ))
                            }
                        </Radio.Group>
                    }
                    {showFilter &&
                        <div style={{ background: "white", borderRadius: "8px", marginRight: "5px" }}>
                           
                            <FiltersMenu menu={() => <RevenueUnitsFilterMenu
                                selectedRadioFilters={selectedRadioFilters as CATEGORY_OVERVIEW_SKU_FILTERS}
                                setSelectedRadioFilters={setSelectedRadioFilters} />}
                                disabled={selectedMSTrendsOvertimeRadio !== CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.MS_PERCENT}
                            />
                        </div>}



                    {additionalTitleComponent &&
                        additionalTitleComponent(
                            PricingCharts.PRICING_DEALS_PPU,
                            PricingCharts.PRICING_DEALS_TRENDS
                        )}
                    <span
                        onClick={onWidgetLinkHandler}
                        role="button"
                        className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover ms-3"
                    >
                        {compare ? `${widgetLinkName || "Compare"}` : ""} {compare ? <BsArrowRight /> : ""}
                    </span>
                </Col>
            </Row>
            {filterPillsComponent && (
                <Row>
                    <Col span={24}>{filterPillsComponent()}</Col>
                </Row>
            )}
            <Row className="d-flex w-100">
                <Col span={24}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                </Col>
            </Row>
        </WidgetCard>
    );
};

export default TrendsWithCompareModalWidget;
